// RULES STUDIO ASSISTANT PANEL — describe the rule, review the draft, apply it.
//
// WHY. Every other tab in this sheet asks the user to already know the answer:
// the Code Editor is an empty textarea whose contents must satisfy an engine
// envelope the panel never states, and getting it wrong does not error — the
// pyarrow service tags "Invalid result format" on every row and the pipeline
// still reports "completed" with zero rows loaded. That is what made the Rules
// Studio unusable for a customer (meeting 2026-08-27, board #168 item A5).
//
// SHAPE. brief -> Draft -> verified preview -> Apply. This is deliberately the
// SAME shape as the agentic-source masters (a brief, a Build button, a preview
// you inspect, then Create) rather than a chat: the user wants one object back
// and a decision to make about it, not a conversation. The panel is a plain
// side-tab, so it needs no session, survives no state, and cannot half-apply.
//
// NOTHING IS WRITTEN HERE. Draft populates local state only; Apply hands the
// draft to the sheet's existing form (onApply -> setFormData), and the user
// still has to press the sheet's own "Save Changes". So there are TWO
// confirmations between a suggestion and a row in Dolt, and the write itself
// goes through the unchanged processing_rule CRUD.
import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Check, AlertTriangle, RotateCcw } from "lucide-react";
import { draftProcessingRule } from "@/services/ruleAssistant";
import ProcessingRuleCodeEditor from "./ProcessingRuleCodeEditor";

const FIELD_CLASS =
  "border-2 border-nav-active bg-card-bg text-page-text focus-visible:border-nav-active-hover focus-visible:ring-0 shadow-none transition-colors";

// Starter briefs. The blank-panel problem is real for this feature: a user who
// does not know what a processing rule CAN do types nothing. These are examples
// of the three shapes (validate / transform / normalize), not templates.
const EXAMPLES = [
  "Reject the row if the value is not a positive number",
  "Strip currency symbols and thousands separators, keep a plain number",
  "Normalise country names to ISO two-letter codes",
];

export default function ProcessingRuleAssistant({ current, context, onApply }) {
  const [instruction, setInstruction] = useState("");
  const [isDrafting, setIsDrafting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(false);

  const isEdit = !!(current && (current.rule_name || current.rule_code));

  const runDraft = async () => {
    const text = instruction.trim();
    if (!text) return;
    setIsDrafting(true);
    setError(null);
    setDraft(null);
    setApplied(false);
    try {
      const res = await draftProcessingRule({
        instruction: text,
        // Send the rule being edited so an instruction like "also allow zero"
        // is applied TO it rather than drafted from nothing.
        current: isEdit ? {
          rule_name: current.rule_name,
          description: current.description,
          rule_code: current.rule_code,
          rule_language: current.rule_language,
          rule_type: current.rule_type,
          input_type: current.input_type,
        } : null,
        context: context || null,
      });
      if (!res?.draft) throw new Error("the assistant returned no draft");
      setDraft(res.draft);
    } catch (err) {
      // fetchapi throws the parsed body; surface the server's own message so a
      // 502 from the LLM hop reads as "what went wrong", not "failed".
      setError(err?.message || "Could not draft the rule. Try rephrasing.");
    } finally {
      setIsDrafting(false);
    }
  };

  const applyDraft = () => {
    if (!draft || !onApply) return;
    onApply(draft);
    setApplied(true);
  };

  return (
    <div className="bg-card-bg rounded-[8px] p-5" data-testid="rule-assistant">
      <div className="mb-4">
        <h3 className="text-base font-medium text-page-text">Rule assistant</h3>
        <p className="text-sm mt-0.5 text-page-muted">
          {isEdit
            ? "Describe what to change and the assistant rewrites this rule for you."
            : "Describe what the rule should do and the assistant writes it for you."}{" "}
          Nothing is saved until you apply the draft and save the rule.
        </p>
      </div>

      {/* Brief */}
      <div>
        <Label htmlFor="rule_instruction" className="text-sm font-medium text-page-text">
          {isEdit ? "What should change?" : "What should this rule do?"}
        </Label>
        <Textarea
          id="rule_instruction"
          data-testid="rule-assistant-instruction"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          rows={4}
          placeholder={isEdit
            ? "e.g. also accept zero, and say which value failed in the message"
            : "e.g. reject any amount that is not a positive number"}
          className={`mt-1 ${FIELD_CLASS}`}
        />
      </div>

      {/* Examples — only while the box is empty, so they never nag. */}
      {!instruction.trim() && !draft && (
        <div className="flex flex-wrap gap-2 mt-3">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => setInstruction(ex)}
              className="text-xs px-2.5 py-1 rounded-[12px] cursor-pointer bg-table-stripe border border-card-border text-page-muted hover:text-nav-active hover:border-nav-active transition-colors"
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mt-4">
        <button
          type="button"
          onClick={runDraft}
          disabled={isDrafting || !instruction.trim()}
          data-testid="rule-assistant-draft"
          className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer border-0 disabled:opacity-50 hover:bg-nav-active-dark transition-colors"
        >
          <Sparkles size={16} />
          {isDrafting ? "Drafting..." : draft ? "Draft again" : "Draft rule"}
        </button>
        {draft && (
          <button
            type="button"
            onClick={() => { setDraft(null); setError(null); setApplied(false); }}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active text-sm font-medium cursor-pointer bg-card-bg hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <RotateCcw size={16} />
            Discard draft
          </button>
        )}
      </div>

      {error && (
        <div
          data-testid="rule-assistant-error"
          className="mt-4 p-3 rounded-[8px] text-sm flex items-start gap-2"
          style={{ background: 'var(--pill-red-bg)', border: '1px solid var(--pill-red-color)' }}
        >
          <AlertTriangle size={16} className="shrink-0 mt-0.5" style={{ color: 'var(--pill-red-dark)' }} />
          <span style={{ color: 'var(--pill-red-dark)' }}>{error}</span>
        </div>
      )}

      {/* PREVIEW. The whole point of the panel: the user reads what the rule
          will do in plain language, sees the code that implements it, and only
          then decides. Never auto-applied. */}
      {draft && (
        <div className="mt-5 pt-5 border-t border-card-border" data-testid="rule-assistant-draft-preview">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h4 className="text-base font-medium text-page-text">Draft</h4>
              <p className="text-sm mt-0.5 text-page-muted">Review it, then apply it to the form.</p>
            </div>
            <button
              type="button"
              onClick={applyDraft}
              data-testid="rule-assistant-apply"
              className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer border-0 hover:bg-nav-active-dark transition-colors"
            >
              <Check size={16} />
              {applied ? "Applied — now Save Changes" : "Apply to rule"}
            </button>
          </div>

          {applied && (
            <div
              data-testid="rule-assistant-applied"
              className="mb-4 p-3 rounded-[8px] text-sm"
              style={{ background: 'var(--pill-amber-bg)', color: 'var(--pill-amber-color)' }}
            >
              The draft is in the form above. It is not stored yet — press
              &ldquo;Save Changes&rdquo; at the top to keep it.
            </div>
          )}

          {/* What it does, in the user's language. */}
          {draft.summary && (
            <div className="mb-4 p-3 rounded-[8px] text-sm bg-table-stripe border border-card-border text-page-text">
              {draft.summary}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm mb-4">
            <div>
              <span className="text-page-muted">Name: </span>
              <span className="font-mono text-page-text" data-testid="rule-assistant-name">{draft.rule_name}</span>
            </div>
            <div>
              <span className="text-page-muted">Type: </span>
              <span className="text-page-text">{draft.rule_type}</span>
            </div>
            <div>
              <span className="text-page-muted">Language: </span>
              <span className="text-page-text">{draft.rule_language}</span>
            </div>
            <div>
              <span className="text-page-muted">Input type: </span>
              <span className="text-page-text">{draft.input_type}</span>
            </div>
            {draft.description && (
              <div className="sm:col-span-2">
                <span className="text-page-muted">Description: </span>
                <span className="text-page-text">{draft.description}</span>
              </div>
            )}
          </div>

          {/* Read-only: this is a preview, not a second editor. Editing happens
              in the Code Editor tab after Apply, so there is exactly one place
              the code can be changed. (Only one tab is mounted at a time, so
              this instance and the Code Editor's never share the DOM.) */}
          <div className="mb-4">
            <ProcessingRuleCodeEditor
              code={draft.rule_code}
              onChange={() => {}}
              language={draft.rule_language}
              label="Drafted code"
              rows={14}
              readOnly
            />
          </div>

          {!!draft.test_cases?.length && (
            <div>
              <Label className="text-xs text-page-muted">
                Test cases ({draft.test_cases.length}) — applied with the draft, runnable in Test Runner
              </Label>
              <div className="mt-2 space-y-2">
                {draft.test_cases.map((tc, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-[8px] text-sm border border-card-border"
                    style={{ background: i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)' }}
                  >
                    <div className="font-medium text-page-text">{tc.name}</div>
                    <div className="mt-1 font-mono text-xs text-page-muted">
                      {JSON.stringify(tc.input)} → {JSON.stringify(tc.expected)}
                    </div>
                    {tc.description && <div className="mt-1 text-xs text-page-muted">{tc.description}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
